import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type MapLayerKey =
  | 'barriers'
  | 'detours'
  | 'routes_barrier_aware'
  | 'routes_gis'
  | 'routes_iteko'
  | 'industrial_ref_book'
  | 'freight_restrictions';

export const MAP_LAYER_KEYS: readonly MapLayerKey[] = [
  'barriers',
  'detours',
  'routes_barrier_aware',
  'routes_gis',
  'routes_iteko',
  'industrial_ref_book',
  'freight_restrictions',
] as const;

export interface MapLayerState {
  readonly key: MapLayerKey;
  readonly visible: boolean;
  /**
   * `disabled=true` grays out the layer in the legend and disables its
   * rendering regardless of `visible`. Used for `routes_iteko` until the
   * ITEKO provider is wired up.
   */
  readonly disabled: boolean;
}

interface MapLayersValue {
  /** Layers in render order — first entry is drawn LAST (= top of z-stack). */
  readonly layers: readonly MapLayerState[];
  readonly toggleVisibility: (key: MapLayerKey) => void;
  /** Moves the layer at `fromIndex` to `toIndex` in the ordering. */
  readonly reorder: (fromIndex: number, toIndex: number) => void;
}

const MapLayersContext = createContext<MapLayersValue | undefined>(undefined);

const DEFAULT_ORDER: readonly MapLayerKey[] = [
  'barriers',
  'detours',
  'routes_barrier_aware',
  'routes_gis',
  'routes_iteko',
  'industrial_ref_book',
  'freight_restrictions',
];

const DISABLED_BY_DEFAULT: ReadonlySet<MapLayerKey> = new Set([
  'routes_iteko',
]);

// Layers that exist but should start hidden (user opts in via the
// legend). The industrial reference layer holds 8.5k points — keeping
// it off by default avoids visual clutter on first load. The freight
// restrictions layer carries hundreds of thousands of segments and is
// only useful at zoom ≥ 9 (the source enforces this); off by default.
const HIDDEN_BY_DEFAULT: ReadonlySet<MapLayerKey> = new Set([
  'industrial_ref_book',
  'freight_restrictions',
]);

interface MapLayersProviderProps {
  readonly children: ReactNode;
}

export function MapLayersProvider({ children }: MapLayersProviderProps) {
  const [layers, setLayers] = useState<readonly MapLayerState[]>(() =>
    DEFAULT_ORDER.map((key) => ({
      key,
      visible: !HIDDEN_BY_DEFAULT.has(key),
      disabled: DISABLED_BY_DEFAULT.has(key),
    })),
  );

  const toggleVisibility = useCallback((key: MapLayerKey) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.key === key && !layer.disabled
          ? { ...layer, visible: !layer.visible }
          : layer,
      ),
    );
  }, []);

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    setLayers((prev) => {
      if (
        fromIndex === toIndex
        || fromIndex < 0
        || toIndex < 0
        || fromIndex >= prev.length
        || toIndex >= prev.length
      ) {
        return prev;
      }
      const copy = [...prev];
      const [moved] = copy.splice(fromIndex, 1);
      if (moved === undefined) {
        return prev;
      }
      copy.splice(toIndex, 0, moved);
      return copy;
    });
  }, []);

  const value = useMemo<MapLayersValue>(
    () => ({ layers, toggleVisibility, reorder }),
    [layers, toggleVisibility, reorder],
  );

  return (
    <MapLayersContext.Provider value={value}>
      {children}
    </MapLayersContext.Provider>
  );
}

export function useMapLayers(): MapLayersValue {
  const ctx = useContext(MapLayersContext);
  if (!ctx) {
    throw new Error('useMapLayers must be used within MapLayersProvider');
  }
  return ctx;
}
